import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { useDispatch } from "react-redux";
import { logoutAsync } from "@/redux/authSlice";
import { AppDispatch, RootState } from "@/redux/store";
import { useNavigate } from "react-router-dom";
import { useToast } from "../use-toast";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { setCode, setCounter } from "@/redux/counterSlice";
import { Badge } from "../badge";
import { useGetStaffByIdQuery } from "@/services/staffsApi";
import { useGetBusinessUnitsQuery } from "@/services/businessUnitApi";
import profileimg from "@/assets/profile-image.svg";
import { breadcrumbs, pageTitles } from "@/lib/constants/header";
import { useGetCounterByIdQuery } from "@/services/counterApi";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const { VITE_VIEW_S3_URL } = import.meta.env;

const Breadcrumb = ({
  currentPath,
  targetPath,
  title,
  pageSetting,
}: {
  currentPath: string;
  targetPath: string;
  title: string;
  pageSetting?: string;
}) => {
  if (!isActiveLink(currentPath, targetPath)) return null;
  return (
    <div className="pl-7 text-sm">
      <span className="text-gray-400 pr-1 font-semibold text-sm">
        {pageSetting}
      </span>{" "}
      <span className="text-gray-400 font-semibold">/</span>
      <span className="pl-1 text-primary font-semibold text-sm">{title}</span>
    </div>
  );
};

const PageTitle = ({
  currentPath,
  targetPath,
  title,
}: {
  currentPath: string;
  targetPath: string;
  title: string;
}) => {
  if (!isActiveLink(currentPath, targetPath)) return null;
  return <p className="text-lg">{title}</p>;
};

const isActiveLink = (currentPath: string, targetPath: string) => {
  if (currentPath == targetPath) return true;
  if (targetPath.endsWith("/") && currentPath.startsWith(targetPath)) {
    return true;
  } else if (
    currentPath.startsWith(targetPath) &&
    !targetPath.includes("facility") &&
    !targetPath.includes("members") &&
    !targetPath.includes("training")
  ) {
    return true;
  }
  return false;
};

export const Header = ({
  toggleMobileSidebar,
}: {
  toggleMobileSidebar?: () => void;
}) => {
  const location = useLocation();
  const counter_number =
    (localStorage.getItem("counter_number") as string) == ""
      ? null
      : Number(localStorage.getItem("counter_number") as string);

  const { toast } = useToast();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch<AppDispatch>();

  const handleLogout = () => {
    dispatch(setCode(null));
    dispatch(setCounter(null));
    dispatch(logoutAsync());
    toast({
      variant: "success",
      title: "Logout",
      description: "You are Successfully Logged Out",
    });
    navigate("/admin/members", { replace: true });
  };

  const orgId = userInfo?.user?.org_id as number;

  const { data: profileInfo } = useGetStaffByIdQuery(
    userInfo?.user?.id as number,
    { skip: !userInfo?.user?.id }
  );

  // Business units API — same pattern as staff table
  const { data: businessUnitsData } = useGetBusinessUnitsQuery(
    { org_id: orgId },
    { skip: !orgId }
  );

  // Resolve business_unit_ids -> names locally (API does not return names)
  const resolvedBusinessUnitNames = useMemo(() => {
    const ids = profileInfo?.business_unit_ids ?? [];
    if (!ids.length || !businessUnitsData?.data) return [];
    const map: Record<number, string> = {};
    businessUnitsData.data.forEach((bu) => { map[bu.id] = bu.name; });
    return ids.map((id) => map[id]).filter(Boolean) as string[];
  }, [profileInfo?.business_unit_ids, businessUnitsData]);

  const { data: counterData } = useGetCounterByIdQuery(
    counter_number as number,
    { skip: !counter_number }
  );

  return (
    <header className="font-poppins sticky top-0 z-20 flex h-16 items-center justify-between border-b bg-white px-1">
      <div className="flex w-full justify-between items-center gap-2 flex-row">
        
        {/* LEFT SIDE */}
        <div className="w-full slg:!w-[calc(100%-280px)] flex flex-row h-full justify-between items-center">
          <div className="flex items-center gap-1 min-w-0 flex-1">
            {/* Hamburger - sirf mobile par */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden shrink-0"
              onClick={toggleMobileSidebar}
            >
              <i className="fa-solid fa-bars text-xl"></i>
            </Button>

            <div className="flex flex-col min-w-0">
              <h1 className="text-2xl font-bold pl-7">
                {pageTitles.map(({ targetPath, title }, index) => (
                  <PageTitle
                    key={index}
                    currentPath={location.pathname}
                    targetPath={targetPath}
                    title={title}
                  />
                ))}
              </h1>
              <div className="hidden sm:flex">
                {breadcrumbs.map(({ targetPath, title, pageSetting }, index) => (
                  <Breadcrumb
                    key={index}
                    currentPath={location.pathname}
                    targetPath={targetPath}
                    title={title}
                    pageSetting={pageSetting}
                  />
                ))}
              </div>
            </div>
          </div>

          {counter_number && (
            <Badge className="text-white mx-2">
              <span className="text-nowrap capitalize">
                Opened Counter: {counterData?.name}
              </span>
            </Badge>
          )}
        </div>

        {/* RIGHT SIDE */}
        <div className="w-fit slg:max-w-[340px] flex flex-row justify-end slg:justify-center items-center gap-3 px-2 slg:px-0">
          <Separator orientation="vertical" className="h-10" />
          <div className="flex flex-row justify-center items-center gap-4">
            <div className="w-8 h-8 border-[1px] border-gray-400 rounded-full justify-center flex items-center">
              <i className="fa-regular fa-envelope"></i>
            </div>
            <div className="w-8 h-8 border-[1px] border-gray-400 rounded-full justify-center flex items-center">
              <i className="fa-regular fa-bell"></i>
            </div>
          </div>
          <Separator orientation="vertical" className="h-10" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <div className="justify-center cursor-pointer items-center flex gap-2">
                  <div className="relative size-8 rounded-full shrink-0">
                    <img
                      id="avatar"
                      src={
                        profileInfo?.profile_img && profileInfo?.profile_img !== ""
                          ? `${VITE_VIEW_S3_URL}/${profileInfo?.profile_img}`
                          : profileimg
                      }
                      loading="lazy"
                      alt={"/profileimg"}
                      className="size-8 rounded-full object-cover relative"
                    />
                  </div>
                  <div className="hidden sm:flex flex-col items-start leading-tight">
                    <h1 className="text-base capitalize font-medium">
                      {userInfo?.user?.first_name}
                    </h1>
                    {profileInfo?.role_name && (
                      <span className="text-xs text-gray-500 capitalize">
                        {profileInfo.role_name}
                      </span>
                    )}
                  </div>
                  <i className="fa-solid fa-caret-down text-sm"></i>
                </div>
              </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 p-0">
              {/* Profile section — horizontal layout */}
              <div className="flex flex-row items-center gap-3 px-4 py-4">
                <div className="relative size-10 rounded-full shrink-0">
                  <img
                    src={
                      profileInfo?.profile_img && profileInfo?.profile_img !== ""
                        ? `${VITE_VIEW_S3_URL}/${profileInfo?.profile_img}`
                        : profileimg
                    }
                    loading="lazy"
                    alt="avatar"
                    className="size-10 rounded-full object-cover"
                  />
                </div>
                <div className="flex flex-col gap-0.5 min-w-0">
                  <p className="text-sm font-semibold capitalize truncate">
                    {userInfo?.user?.first_name}
                  </p>
                  {/* Role badge + Business Units on same row */}
                  <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                    {profileInfo?.role_name && (
                      <Badge className="text-white text-xs capitalize px-2 py-0.5 shrink-0">
                        {profileInfo.role_name}
                      </Badge>
                    )}
                    {resolvedBusinessUnitNames.length > 0 && (() => {
                      const visible = resolvedBusinessUnitNames.slice(0, 3);
                      const remaining = resolvedBusinessUnitNames.slice(3);
                      return (
                        <span className="flex items-center gap-1 flex-wrap">
                          <span className="text-xs text-gray-500 capitalize">
                            {visible.join(" • ")}
                          </span>
                          {remaining.length > 0 && (
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-xs font-medium text-primary cursor-default">
                                    +{remaining.length}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-[220px]">
                                  <p className="text-xs capitalize">{remaining.join(" • ")}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
              <DropdownMenuSeparator className="m-0" />
              <DropdownMenuItem
                className="cursor-pointer px-4 py-2.5 text-red-500 focus:text-red-500"
                onClick={handleLogout}
              >
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

      </div>
    </header>
  );
};